// components/industry-templates/CharityEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Heart, Target, Users, DollarSign } from 'lucide-react';

export const CharityEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      <Card className="border-0 bg-gradient-to-br from-green-500/20 to-emerald-500/20 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 text-2xl">
            <Heart className="w-8 h-8" style={{ color: theme.primary }} />
            Our Cause
          </CardTitle>
        </CardHeader>
        <CardContent>
          {industryData?.cause && (
            <div className="p-6 bg-white/10 rounded-xl">
              <h3 className="text-2xl font-bold text-white mb-3">{industryData.cause.name}</h3>
              <p className="text-slate-300 leading-relaxed">{industryData.cause.description}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {industryData?.fundraising_goal && (
        <Card className="border-2" style={{ borderColor: theme.primary, backgroundColor: 'rgba(22, 163, 74, 0.1)' }}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Target className="w-6 h-6" style={{ color: theme.primary }} />
              Fundraising Goal
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <p className="text-5xl font-bold mb-2" style={{ color: theme.primary }}>
                ₹{industryData.fundraising_goal.target?.toLocaleString()}
              </p>
              {industryData.fundraising_goal.raised && (
                <>
                  <p className="text-white text-xl mb-4">
                    ₹{industryData.fundraising_goal.raised?.toLocaleString()} raised
                  </p>
                  <div className="w-full bg-white/10 rounded-full h-4">
                    <div
                      className="h-4 rounded-full transition-all"
                      style={{
                        backgroundColor: theme.primary,
                        width: `${(industryData.fundraising_goal.raised / industryData.fundraising_goal.target) * 100}%`,
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {industryData?.beneficiaries && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-6 h-6" style={{ color: theme.primary }} />
              Impact & Beneficiaries
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {industryData.beneficiaries.count && (
                <div className="p-4 bg-white/5 rounded-lg text-center">
                  <p className="text-3xl font-bold" style={{ color: theme.primary }}>
                    {industryData.beneficiaries.count}+
                  </p>
                  <p className="text-sm text-slate-400">Lives Impacted</p>
                </div>
              )}
              {industryData.beneficiaries.communities && (
                <div className="p-4 bg-white/5 rounded-lg text-center">
                  <p className="text-3xl font-bold" style={{ color: theme.primary }}>
                    {industryData.beneficiaries.communities}
                  </p>
                  <p className="text-sm text-slate-400">Communities</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {industryData?.donation_tiers && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <DollarSign className="w-6 h-6" style={{ color: theme.primary }} />
              Donation Tiers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {industryData.donation_tiers.map((tier: any, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg border-l-4" style={{ borderColor: theme.primary }}>
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-bold text-white">{tier.name}</h4>
                      <p className="text-sm text-slate-400">{tier.benefits}</p>
                    </div>
                    <Badge style={{ backgroundColor: theme.primary }} className="text-lg py-2 px-4">
                      ₹{tier.amount}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
